import { motion, useReducedMotion } from 'framer-motion';
import { X, Clock, BookOpen, Gem, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';

export default function ModuleDetailSheet({ module, open, onClose, onNavigate }) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();

  if (!module) return null;

  // Backend returns resolved language; fallback to _tr/_en for mock compat
  const title = module.title || (lang === 'tr' ? module.title_tr : module.title_en) || '';
  const description = module.description || (lang === 'tr' ? module.description_tr : module.description_en) || '';

  const ctaKey = {
    not_started: 'cabin.module.cta.start',
    in_progress:  'cabin.module.cta.continue',
    completed:    'cabin.module.cta.retry',
    locked:       'cabin.module.cta.locked',
  }[module.status] || 'cabin.module.cta.start';

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[400px] p-0 overflow-y-auto">
        {/* Gradient header */}
        <div className={`bg-gradient-to-br ${module.gradient || 'from-sky-500 to-blue-600'} p-6 relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label={t('common.close') || 'Close'}
            data-testid="module-detail-sheet-close"
          >
            <X size={18} aria-hidden="true" />
          </button>
          <span className="text-5xl block mb-3" aria-hidden="true">{module.emoji}</span>
          <SheetHeader>
            <SheetTitle className="text-white text-xl font-black text-left">
              {title}
            </SheetTitle>
          </SheetHeader>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-4">
          {description && (
            <p className="text-sm text-[var(--text-muted)]">{description}</p>
          )}

          {/* Meta */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock size={16} className="text-[var(--text-muted)]" aria-hidden="true" />
              <span className="text-[var(--text-muted)]">
                {interpolate(t('cabin.roadmap.module_time'), { time: module.estimatedTime })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <BookOpen size={16} className="text-[var(--text-muted)]" aria-hidden="true" />
              <span className="text-[var(--text-muted)]">
                {interpolate(t('cabin.roadmap.module_units'), {
                  units: module.unitCount,
                  lessons: module.lessonCount,
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Gem size={16} className="text-[var(--brand-primary)]" aria-hidden="true" />
              <span className="font-semibold text-[var(--brand-primary)]">
                {interpolate(t('cabin.roadmap.module_miles'), { miles: module.milesReward })}
              </span>
            </div>
          </div>

          {/* Progress */}
          {module.status === 'in_progress' && (
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[var(--text-muted)]">İlerleme</span>
                <span className="font-semibold text-[var(--brand-primary)]">{module.progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--brand-primary)] rounded-full transition-all"
                  style={{ width: `${module.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* CTA button */}
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.99 }}
            onClick={() => onNavigate?.(module)}
            disabled={module.status === 'locked'}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid="module-detail-sheet-cta"
          >
            {t(ctaKey)} <ChevronRight size={16} aria-hidden="true" />
          </motion.button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
